import React, { useEffect, useState } from 'react';
import { FaBell, FaEdit, FaUser, FaExclamationTriangle } from "react-icons/fa";
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import NotificationManagement from '../components/NotificationManagement';
import Complaints from './Complaints';
import '../css/Sidebar.css';
import '../css/Navbar.css';
import '../css/Profile.css';
import { authAPI, usersAPI, vehiclesAPI } from '../api/api';
import pushNotificationService from '../services/PushNotificationService';

// Import password validator
import { validatePassword as validatePasswordStrength } from '../utils/passwordValidator';

const Profile = ({ isSidebarClosed, setIsSidebarClosed }) => {
  const [user, setUser] = useState(null);
  const [cars, setCars] = useState([]);
  const [colleagues, setColleagues] = useState([]);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    newPasswordRepeat: ''
  });
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [lastPasswordChange, setLastPasswordChange] = useState(null);
  const [canChangePassword, setCanChangePassword] = useState(true);

  // Push notification state
  const [pushPermission, setPushPermission] = useState('default');
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushMessage, setPushMessage] = useState('');

  useEffect(() => {
    const parsedUser = authAPI.getCurrentUser();
    if (parsedUser) {
      setUser(parsedUser);

      const fetchCars = async () => {
        try {
          const carsData = await vehiclesAPI.getAllVehiclesForUser(parsedUser);
          setCars(carsData);
        } catch (error) {
          console.error('Error fetching cars:', error);
          setCars([]);
        }
      };

      const fetchColleagues = async () => {
        try {
          const users = await usersAPI.getClientUsers();
          setColleagues(users.filter(u => u.id !== parsedUser.id));
        } catch (error) {
          console.error('Error fetching colleagues:', error);
        }
      };

      fetchCars();
      fetchColleagues();

      // Jelszó változtatási limit ellenőrzése
      const lastChange = localStorage.getItem(`lastPasswordChange_${parsedUser.id}`);
      if (lastChange) {
        const lastChangeDate = new Date(lastChange);
        const now = new Date();
        const daysSinceLastChange = (now - lastChangeDate) / (1000 * 60 * 60 * 24);

        if (daysSinceLastChange < 7) {
          setCanChangePassword(false);
          setLastPasswordChange(lastChangeDate);
        }
      }

      // Push notification initialization
      const initPushNotifications = async () => {
        try {
          await pushNotificationService.init();
          const permission = await pushNotificationService.getPermissionStatus();
          setPushPermission(permission);
          const isSubscribed = await pushNotificationService.isSubscribed();
          setPushSubscribed(isSubscribed);
        } catch (error) {
          console.error('Push notification init error:', error);
        }
      };

      initPushNotifications();
    } else {
      window.location.href = '/login';
    }
  }, []);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    // Validáció
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.newPasswordRepeat) {
      setPasswordMessage('Minden mező kitöltése kötelező');
      return;
    }

    // Erős jelszó validáció
    const passwordValidation = validatePasswordStrength(passwordForm.newPassword);
    if (!passwordValidation.isValid) {
      // Megjelenítjük az összes hibát
      setPasswordMessage(passwordValidation.errors.join(', '));
      return;
    }

    if (passwordForm.newPassword !== passwordForm.newPasswordRepeat) {
      setPasswordMessage('Az új jelszó és a megerősítés nem egyezik');
      return;
    }

    try {
      setPasswordLoading(true);
      setPasswordMessage('');

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: user.email,
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword,
          newPasswordRepeat: passwordForm.newPasswordRepeat
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Sikeres jelszó változtatás - mentjük a dátumot
        const now = new Date();
        localStorage.setItem(`lastPasswordChange_${user.id}`, now.toISOString());
        setLastPasswordChange(now);
        setCanChangePassword(false);

        setPasswordMessage('✅ A jelszó sikeresen megváltozott! Következő jelszó módosítás: 7 nap múlva.');
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          newPasswordRepeat: ''
        });
        setShowPasswordForm(false);

        // 5 másodperc után eltüntetjük az üzenetet
        setTimeout(() => {
          setPasswordMessage('');
        }, 5000);
      } else {
        setPasswordMessage(result.message || 'Hiba történt a jelszó módosítása során');
      }
    } catch (error) {
      console.error('Jelszó módosítási hiba:', error);
      setPasswordMessage('Hálózati hiba történt');
    } finally {
      setPasswordLoading(false);
    }
  };

  // Push notification handlers
  const handleEnablePushNotifications = async () => {
    try {
      setPushLoading(true);
      setPushMessage('');

      const result = await pushNotificationService.subscribe();

      if (result.success) {
        setPushSubscribed(true);
        setPushPermission('granted');
        setPushMessage('✅ Push értesítések sikeresen engedélyezve!');
        setTimeout(() => setPushMessage(''), 5000);
      } else {
        setPushMessage('❌ ' + (result.message || 'Hiba történt a push értesítések engedélyezése során'));
      }
    } catch (error) {
      console.error('Push subscription error:', error);
      setPushMessage('❌ Hiba történt: ' + error.message);
    } finally {
      setPushLoading(false);
    }
  };

  const handleDisablePushNotifications = async () => {
    try {
      setPushLoading(true);
      setPushMessage('');

      const result = await pushNotificationService.unsubscribe();

      if (result.success) {
        setPushSubscribed(false);
        setPushMessage('✅ Push értesítések letiltva.');
        setTimeout(() => setPushMessage(''), 5000);
      } else {
        setPushMessage('❌ ' + (result.message || 'Hiba történt a push értesítések letiltása során'));
      }
    } catch (error) {
      console.error('Push unsubscribe error:', error);
      setPushMessage('❌ Hiba történt: ' + error.message);
    } finally {
      setPushLoading(false);
    }
  };


  if (!user) {
    return <div className="profile-container"><div className="profile-card">Kérlek, jelentkezz be a profilod megtekintéséhez.</div></div>;
  }

  return (
    <div className='profile-page'>
      <Navbar isSidebarClosed={isSidebarClosed} setIsSidebarClosed={setIsSidebarClosed} />
      <Sidebar isCollapsed={isSidebarClosed} setIsCollapsed={setIsSidebarClosed} />
      <div className={`page-content${isSidebarClosed ? ' collapsed' : ''}`}> 
        <div className="profile-container">
          {/* Tab választó */}
          <div className="profile-tabs-row">
            <button
              className={`profile-tab${activeTab === 'profile' ? ' profile-tab-active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              <FaUser style={{marginRight: 4}} /> Profil
            </button>
            <button
              className={`profile-tab${activeTab === 'notifications' ? ' profile-tab-active' : ''}`}
              onClick={() => setActiveTab('notifications')}
            >
              <FaBell style={{marginRight: 4}} /> Értesítések
            </button>
            <button
              className={`profile-tab${activeTab === 'complaints' ? ' profile-tab-active' : ''}`}
              onClick={() => setActiveTab('complaints')}
            >
              <FaExclamationTriangle style={{marginRight: 4}} /> Reklamációk
            </button>
          </div>

          {/* Profil fejléc */}
          <div className="profile-header-card">
            <div className="profile-avatar">
              {user.avatar ? <img src={user.avatar} alt="Profilkép" /> : <div className="avatar-placeholder">{user.name[0]}</div>}
            </div>
            <div className="profile-header-info">
              <div className="profile-header-name">{user.name}</div>
              <div className="profile-header-role">
                {user.role === 'admin' ? 'Admin' : 'Felhasználó'}
              </div>
              <div className="profile-header-email">{user.email}</div>
            </div>
          </div>

          {activeTab === 'notifications' ? (
            <>
              {/* Push értesítések beállítása */}
              <div className="profile-section-card">
                <div className="profile-section-title">Push értesítések</div>

                {pushMessage && (
                  <div className={`password-message ${pushMessage.includes('✅') ? 'success' : 'error'}`}>
                    {pushMessage}
                  </div>
                )}

                <div className="push-notification-section">
                  <div className="push-status">
                    <strong>Állapot:</strong>{' '}
                    {pushPermission === 'granted' && pushSubscribed ? (
                      <span style={{ color: 'green' }}>✅ Engedélyezve</span>
                    ) : pushPermission === 'denied' ? (
                      <span style={{ color: 'red' }}>❌ Letiltva a böngészőben</span>
                    ) : (
                      <span style={{ color: 'orange' }}>⚠️ Nincs engedélyezve</span>
                    )}
                  </div>

                  <div className="push-actions" style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {!pushSubscribed && pushPermission !== 'denied' && (
                      <button
                        onClick={handleEnablePushNotifications}
                        disabled={pushLoading}
                        className="password-save-btn"
                      >
                        {pushLoading ? 'Feldolgozás...' : 'Push értesítések engedélyezése'}
                      </button>
                    )}

                    {pushSubscribed && (
                      <>
                        <button
                          onClick={handleDisablePushNotifications}
                          disabled={pushLoading}
                          className="password-cancel-btn"
                        >
                          {pushLoading ? 'Feldolgozás...' : 'Push értesítések letiltása'}
                        </button>
                      </>
                    )}
                  </div>

                  {pushPermission === 'denied' && (
                    <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#fff3cd', borderRadius: '4px' }}>
                      <strong>⚠️ Figyelem!</strong> A push értesítések le vannak tiltva a böngészőben.
                      A beállítások módosításához engedélyezd az értesítéseket a böngésző beállításaiban.
                    </div>
                  )}

                  <div style={{ marginTop: '1rem', fontSize: '0.9em', color: '#666' }}>
                    A push értesítések lehetővé teszik, hogy valós idejű értesítéseket kapj,
                    még akkor is, ha az alkalmazás nincs megnyitva.
                  </div>
                </div>
              </div>

              {/* Értesítési beállítások */}
              <NotificationManagement />
            </>
          ) : activeTab === 'complaints' ? (
            <Complaints isSidebarClosed={isSidebarClosed} setIsSidebarClosed={setIsSidebarClosed} />
          ) : (
            <>
              {/* Személyes információk */}
              <div className="profile-section-card">
                <div className="profile-section-title">Személyes információk</div>
                <div className="profile-info-grid">
                  <div><span>Teljes név:</span> {user.name}</div>
                  <div><span>Email:</span> {user.email}</div>
                  <div><span>Születési dátum:</span> {user.dob || 'Nincs megadva'}</div>
                  <div><span>Telefonszám:</span> {user.phone || 'Nincs megadva'}</div>
                  <div><span>Szerepkör:</span> {user.role === 'admin' ? 'Admin' : 'Felhasználó'}</div>
                </div>
              </div>

                             {/* Autóim */}
               <div className="profile-section-card">
                 <div className="profile-section-title">
                   {user.role === 'admin' ? 'Cég autói' : 'Autóim'}
                 </div>
                 <div className="profile-cars-list">
                   {cars.length === 0 ? (
                     <div>{user.role === 'admin' ? 'Nincs autó a cégben.' : 'Nincs autó hozzád rendelve.'}</div>
                   ) : (
                     cars.map(car => (
                       <div key={car.plate_number} className="profile-car-item">
                         <div className='profile-car-item-info'>
                           <b>{car.brand} {car.model}</b>
                           {user.role === 'admin' && car.owner && (
                             <span style={{fontSize: '0.8em', color: '#666', display: 'block'}}>
                               Tulajdonos: {car.owner}
                             </span>
                           )}
                         </div>
                         <div className='profile-car-item-plate'>Rendszám: {car.plate_number}</div>
                       </div>
                     ))
                   )}
                 </div>
               </div>

              {/* Munkatársak */}
              <div className="profile-section-card">
                <div className="profile-section-title">Munkatársak</div>
                <div className="profile-colleagues-list">
                  {colleagues.length === 0 ? (
                    <div>Nincs más munkatárs.</div>
                  ) : (
                    colleagues.map(col => (
                      <div key={col.email} className="profile-colleague-item">
                        <div className='profile-colleague-item-name'><b>{col.name}</b> ({col.role === 'admin' ? 'Admin' : 'Felhasználó'})</div>
                        <div className='profile-colleague-item-email'>{col.email}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Jelszó módosítás */}
              <div className="profile-section-card">
                <div className="profile-section-title">
                  Jelszó módosítás 
                  {canChangePassword ? (
                    <button className="edit-btn" onClick={()=>setShowPasswordForm(e=>!e)}>
                      <FaEdit /> Szerkesztés
                    </button>
                  ) : (
                    <span className="password-limit-info">
                      Következő módosítás: {lastPasswordChange ? 
                        new Date(lastPasswordChange.getTime() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('hu-HU') : 
                        '7 nap múlva'
                      }
                    </span>
                  )}
                </div>
                
                {!canChangePassword && (
                  <div className="password-limit-message">
                    <span>Heti maximum 1x módosíthatod a jelszavad. Utolsó módosítás: {lastPasswordChange ? lastPasswordChange.toLocaleDateString('hu-HU') : 'ismeretlen'}</span>
                  </div>
                )}
                
                <div className={`profile-password-form-anim${showPasswordForm ? ' open' : ''}`}>
                  {passwordMessage && (
                    <div className={`password-message ${passwordMessage.includes('sikeresen') ? 'success' : 'error'}`}>
                      {passwordMessage}
                    </div>
                  )}
                                     <form className="profile-password-form" onSubmit={handlePasswordSubmit}>
                     {/* Rejtett felhasználónév mező az akadálymentességhez */}
                     <input
                       type="text"
                       name="username"
                       value={user.email}
                       autoComplete="username"
                       style={{ display: 'none' }}
                       readOnly
                     />
                     <input 
                        type="password" 
                        name="currentPassword"
                        placeholder="Jelenlegi jelszó" 
                        value={passwordForm.currentPassword}
                        onChange={handlePasswordChange}
                        required 
                        disabled={!canChangePassword}
                        autoComplete="current-password"
                      />
                                         <input 
                       type="password" 
                       name="newPassword"
                       placeholder="Új jelszó (min. 8 karakter)"
                       value={passwordForm.newPassword}
                       onChange={handlePasswordChange}
                       required 
                       disabled={!canChangePassword}
                       autoComplete="new-password"
                     />
                                         <input 
                       type="password" 
                       name="newPasswordRepeat"
                       placeholder="Új jelszó megerősítése" 
                       value={passwordForm.newPasswordRepeat}
                       onChange={handlePasswordChange}
                       required 
                       disabled={!canChangePassword}
                       autoComplete="new-password"
                     />
                    <div className="password-form-actions">
                      <button 
                        type="submit" 
                        disabled={passwordLoading || !canChangePassword}
                        className="password-save-btn"
                      >
                        {passwordLoading ? 'Feldolgozás...' : 'Mentés'}
                      </button>
                      <button 
                        type="button" 
                        onClick={() => {
                          setShowPasswordForm(false);
                          setPasswordForm({
                            currentPassword: '',
                            newPassword: '',
                            newPasswordRepeat: ''
                          });
                          setPasswordMessage('');
                        }}
                        className="password-cancel-btn"
                      >
                        Mégse
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile; 